import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    User,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail,
    sendEmailVerification,
    UserCredential,
    signInAnonymously,
    linkWithCredential,
    EmailAuthProvider,
} from 'firebase/auth';
import { auth, db, functions } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

export interface UserProfile {
    tier: 'free' | 'pro';
    usage: {
        generateCount: number;
        lastResetDate: string;
    };
}

interface AuthContextType {
    currentUser: User | null;
    loading: boolean;
    profile: UserProfile | null;
    profileLoading: boolean;
    register: (email: string, password: string) => Promise<UserCredential>;
    login: (email: string, password: string) => Promise<UserCredential>;
    logout: () => Promise<void>;
    refreshProfile: () => Promise<void>;
    sendPasswordReset: (email: string) => Promise<void>;
    sendVerificationEmail: (user: User) => Promise<void>;
    signInAnonymously: () => Promise<UserCredential>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within an AuthProvider');
    return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [profileLoading, setProfileLoading] = useState(true);
    const createUserProfile = httpsCallable(functions, 'createUserProfile');

    // 🔥 Profil-Listener – JETZT AUCH FÜR ANONYME NUTZER!
    useEffect(() => {
        if (!currentUser) {
            setProfile(null);
            setProfileLoading(false);
            return;
        }

        // Bei anonymen Nutzern: trotzdem Firestore-Dokument laden
        // (Das Dokument wurde beim anonymen Login durch createUserProfile angelegt)
        setProfileLoading(true);
        const userDocRef = doc(db, 'users', currentUser.uid);

        const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data() as UserProfile;
                setProfile(data);
            } else {
                // Fallback, falls Dokument noch nicht existiert (sollte nicht vorkommen)
                setProfile({ tier: 'free', usage: { generateCount: 0, lastResetDate: '' } });
            }
            setProfileLoading(false);
        }, (error) => {
            console.error('Fehler beim Laden des Profils:', error);
            setProfile(null);
            setProfileLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser]); // Keine Abhängigkeit von emailVerified – jetzt für alle User

    const refreshProfile = async () => {
        if (!currentUser) {
            setProfile(null);
            setProfileLoading(false);
            return;
        }
        // Manuelles Neuladen: einmalig onSnapshot abonnieren und sofort beenden
        setProfileLoading(true);
        const userDocRef = doc(db, 'users', currentUser.uid);
        const unsub = onSnapshot(userDocRef, (snap) => {
            if (snap.exists()) {
                setProfile(snap.data() as UserProfile);
            } else {
                setProfile({ tier: 'free', usage: { generateCount: 0, lastResetDate: '' } });
            }
            setProfileLoading(false);
            unsub();
        });
    };

    // Anonyme Anmeldung
    const handleAnonymousSignIn = async () => {
        const userCredential = await signInAnonymously(auth);
        // Profil in Firestore anlegen (falls nicht vorhanden)
        try {
            await createUserProfile();
        } catch (error) {
            console.warn('Profil könnte bereits existieren oder Fehler:', error);
        }
        return userCredential;
    };

    // Registrierung – mit oder ohne Verknüpfung
    const register = async (email: string, password: string) => {
        let userCredential: UserCredential;
        if (currentUser && currentUser.isAnonymous) {
            const credential = EmailAuthProvider.credential(email, password);
            userCredential = await linkWithCredential(currentUser, credential);
            await sendEmailVerification(userCredential.user, {
                url: window.location.origin + '/email-bestaetigt',
                handleCodeInApp: false,
            });
        } else {
            userCredential = await createUserWithEmailAndPassword(auth, email, password);
            await sendEmailVerification(userCredential.user, {
                url: window.location.origin + '/email-bestaetigt',
                handleCodeInApp: false,
            });
            await createUserProfile();
        }
        return userCredential;
    };

    const login = async (email: string, password: string) => {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        if (!userCredential.user.emailVerified) {
            throw new Error('EMAIL_NOT_VERIFIED');
        }
        return userCredential;
    };

    // 🔥 Logout für anonyme Nutzer blockieren
    const logout = async () => {
        if (currentUser?.isAnonymous) {
            console.warn('Anonyme Nutzer können sich nicht abmelden.');
            return;
        }
        await signOut(auth);
        setProfile(null);
    };

    const sendPasswordReset = async (email: string) => {
        await sendPasswordResetEmail(auth, email);
    };

    const sendVerificationEmail = async (user: User) => {
        const continueUrl = `${window.location.origin}/email-bestaetigt`;
        await sendEmailVerification(user, {
            url: continueUrl,
            handleCodeInApp: false,
        });
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
            setLoading(false);
        });
        return unsubscribe;
    }, []);

    const value = {
        currentUser,
        loading,
        profile,
        profileLoading,
        register,
        login,
        logout,
        refreshProfile,
        sendPasswordReset,
        sendVerificationEmail,
        signInAnonymously: handleAnonymousSignIn,
    };

    return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};